import { Dispatch, FC, SetStateAction } from 'react';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { MasterTournamentTableLoader } from './MasterTournamentTableLoader';
import noTournamentData from '../../../../assets/images/home/speakup-game/no-master-tounament.svg';
import { useTranslation } from 'react-i18next';
import { HiArrowNarrowDown, HiArrowNarrowUp } from 'react-icons/hi';
import { RxDash } from 'react-icons/rx';

import { Tooltip } from 'primereact/tooltip';
import { TournamentStandingContainer } from './MasterTournament.style';
import { useAppDispatch } from '../../../../core/hooks/hooks';
import { resetDialogue, setDialogue } from '../../../../core/store/slices/dialogueSlice';
import successFirstIcon from './../../../../assets/images/Winner.svg';
import successIcon from './../../../../assets/images/Leader-pana.png';

interface MasterProps {
	data: {
		id?: string;
		rank: number;
		status: string;
		isMyTeam: boolean;
		teams: string;
		rounds: number;
		lastScore: number;
		points: number;
	}[];
	tournament: string;
	loading: boolean;
	next?: string;
	prev?: string;
	setStandingNo: Dispatch<SetStateAction<number>>;
	standingNo: number;
	status: string;
}

const MasterTournamentTable: FC<MasterProps> = ({ data, loading, next, prev, setStandingNo, standingNo, tournament, status }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const tableHead = [t('Teams'), t('Rounds'), t('lastScore'), t('Points')];

	if (loading) {
		return <MasterTournamentTableLoader />;
	}

	const showWinnerPopup = (myTeam) => {
		if (myTeam.isMyTeam && status === 'finished') {
			dispatch(
				setDialogue({
					show: true,
					type: 'Success',
					acceptColor: '#01ABC1',
					textColor: '#01ABC1',
					image: myTeam.rank === 1 ? successFirstIcon : successIcon,
					hasAction: false,
					acceptLabel: <div>{t('Continue')}</div>,
					title: `${myTeam.rank}${t('rank', { count: myTeam.rank, ordinal: true })}`,
					text: `Your Team came in ${t(`${myTeam.rank}${t('rank', { count: myTeam.rank })}`)} in '${tournament}' Tournament.`,
					onAccept: () => {
						dispatch(resetDialogue());
					},
				}),
			);
		}
	};
	return (
		<TournamentStandingContainer>
			<div className="home__headers">
				{data ? (
					<>
						<div className=" header__toggle__tab flex items-center justify-between bg-[#01ABC1]">
							<div className="text__default font-manropeBold text-white">{tournament}</div>
							<div className="flex flex-row items-center gap-4 text-white">
								<BsChevronLeft
									onClick={() => setStandingNo(prev ? standingNo - 1 : 1)}
									className={`${!prev ? 'pointer-events-none cursor-default opacity-50' : 'cursor-pointer'}`}
								/>
								<BsChevronRight
									onClick={() => setStandingNo(next ? standingNo + 1 : 1)}
									className={`${!next ? 'pointer-events-none cursor-default opacity-50' : 'cursor-pointer'}`}
								/>
							</div>
						</div>
						{data.length > 0 ? (
							<div className="standing__table_container overflow-x-hidden overflow-y-hidden rounded-bl-3xl rounded-br-3xl border">
								<table className="standing__table w-full bg-white">
									<thead>
										<tr className="border-b">
											{tableHead.map((h, i) => (
												<th
													className={`text__default p-3 py-3 font-manropeBold text-xs lg:px-4 xl:px-6 xl:pt-3 xl:pb-3 xl:text-sm 2xl:text-base ${
														i === 0 ? 'text-left 2xl:columns-3' : 'text-center'
													}`}
													key={i}
												>
													{h}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{data.map((data, i) => {
											return (
												<tr
													key={`master-tournament-${data.id}-${i}`}
													className={`group font-manropeSemiBold text-xs text-slate-800 transition-colors duration-75 ${
														data.isMyTeam && 'cursor-pointer bg-[#0C7C8A1A] text-[#01ABC1]'
													} `}
													onClick={() => showWinnerPopup(data)}
												>
													<td className="flex h-full flex-row items-center gap-2 px-3 text-[#404040] group-hover:text-[#01ABC1] ">
														<div
															className={`text__default flex items-center gap-1 ${data.status === 'up' && 'text-[#65A743]'} ${
																data.status === 'down' && 'text-[#FF4A4A]'
															}`}
														>
															{data.status === 'up' ? <HiArrowNarrowUp /> : data.status === 'down' ? <HiArrowNarrowDown /> : <RxDash />}
															<span>{data.rank}</span>
														</div>
														<Tooltip target=".team__name " />
														<span
															className="text__default team__name"
															data-pr-tooltip={data.teams}
															data-pr-position="top"
															data-pr-at="left+10 top-10"
														>
															{data.teams?.length > 10 ? data.teams.slice(0, 10) + '...' : data.teams}
														</span>
													</td>
													<td className="text__default px-2 text-center group-hover:text-[#01ABC1]  lg:px-4 lg:py-3 xl:px-6">
														<div>{data.rounds}</div>
													</td>
													<td className="text__default px-2 text-center text-[#404040] group-hover:text-[#01ABC1] lg:px-4 lg:py-3 xl:px-6">
														<div>{data.lastScore}</div>
													</td>
													<td className="text__default px-2 text-center text-[#65A743] group-hover:text-[#01ABC1] lg:px-4 lg:py-3 xl:px-6">
														<div>{data.points}</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						) : (
							<>
								<div className="standing__table_container overflow-x-hidden overflow-y-hidden rounded-bl-3xl rounded-br-3xl border">
									<table className="standing__table w-full bg-white">
										<div className="flex flex-col items-center justify-center bg-white py-10">
											<img src={noTournamentData} alt="no master data" className="h-[12em] w-[12em]" />
											<span className="text__default mx-4 mt-2 text-center font-manropeSemiBold text-[#afafaf]">{t('noTournamentStanding')}</span>
										</div>
									</table>
								</div>
							</>
						)}
					</>
				) : (
					<>
						<div className=" header__toggle__tab flex items-center justify-between bg-[#01ABC1]">
							<div className="text__default font-manropeBold text-white">{t('no_running_tournaments')}</div>
							<div className="flex flex-row items-center gap-4 text-white">
								<BsChevronLeft
									onClick={() => setStandingNo(prev ? standingNo - 1 : 1)}
									className={`${!prev ? 'pointer-events-none cursor-default opacity-50' : 'cursor-pointer'}`}
								/>
								<BsChevronRight
									onClick={() => setStandingNo(next ? standingNo + 1 : 1)}
									className={`${!next ? 'pointer-events-none cursor-default opacity-50' : 'cursor-pointer'}`}
								/>
							</div>
						</div>

						<div className="standing__table_container flex flex-col items-center justify-center bg-white py-10">
							<img src={noTournamentData} alt="no master data" className="h-48 w-48" />
							<span className="text__default mt-2 font-manropeSemiBold text-lg text-[#afafaf]">{t('noMasterTournament')}</span>
						</div>
					</>
				)}
			</div>
		</TournamentStandingContainer>
	);
};

export default MasterTournamentTable;
